// Webapplication/backend/src/components/BeeVideo/BeeVideocontainer.jsx

import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';

// FFmpeg instance scoped outside so it's shared across calls
const ffmpeg = new FFmpeg();

const BeeVideoContainer = {
    /**
     * Creates the specifics of the url needed for the endpoint
     * @param {string} filePath - Passed file path. Should be a h24 or mp4 file.
     * @returns {string} - New updated file path
     */
    createSpecificVideoUrl(filePath) {
        const splitPath = filePath.split("appmais/");
        const latterHalfPath = splitPath.length > 1 ? splitPath[1] : "";
        return latterHalfPath.replace(/\.h264$/, ".mp4");
    },

    /**
     * Does the same objective as video createSpecificVideoUrl but with audio
     * @param filePath - Passed filepath.
     * @returns {string} - New updated file path
     */
    createSpecificAudioUrl(filePath) {
        const splitPath = filePath.split("appmais/");
        let relativePath = splitPath.length > 1 ? splitPath[1] : "";
        relativePath = relativePath.replace("/video/", "/audio/");
        return relativePath.replace(/\.(h264|mp4)$/, ".wav");
    },

    /**
     * Fetch the filepath for a given hive/date/time.
     */
    async fetchVideoFilePath(apiUrl, hive, date, time) {
        const response = await fetch(
            `${apiUrl}/api/videofiles/${hive}/${date}/${time}/filepath`
        );
        if (!response.ok) throw new Error("Failed to fetch video filepath");
        return response.json();
    },

    /**
     * Checks if the data at the specified URL exists by making a HEAD request.
     *
     * @param {string} url - The URL to check.
     * @returns {Promise<boolean>} A promise that resolves to true if the resource exists, otherwise false.
     */
    async checkDataExists(url) {
        try {
            const res = await fetch(url, { method: "HEAD" });
            return res.ok;
        } catch (err) {
            console.error("Error checking data:", err);
            return false;
        }
    },

    /**
     * Calculates and sends a "good" amplitude based on the peaks of the initial audio file.
     * @async
     * @param {Uint8Array} audioFile - A typed array containing the raw binary data of the audio file.
     * @returns {Promise<number>} - A promise that resolves to the calculated amplification value in decibels.
     *
     */
    async calculateAmplificationDB(audioFile) {
        const audioContext = new window.AudioContext();
        const arrayBuffer = audioFile.slice(0).buffer;

        const audioBuffer = await new Promise((resolve, reject) => {
            audioContext.decodeAudioData(arrayBuffer, resolve, reject);
        });

        let currentPeak = 0;
        for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
            const channelData = audioBuffer.getChannelData(channel);
            const channelPeak = channelData.reduce(
                (max, sample) => Math.max(max, Math.abs(sample)),
                0
            );
            currentPeak = Math.max(currentPeak, channelPeak);
        }

        const targetPeak = 0.95;
        if (currentPeak === 0) return 0;
        const gainFactor = targetPeak / currentPeak;
        return 20 * Math.log10(gainFactor);
    },

    /**
     * Processes a video and audio url and combines them into one. During this process
     * the audio is also amplified as well. The processed video url is temporarily stored
     * in the ffmpeg virtual file system and should be deleted upon ffmpeg.terminate().
     * @param {string} videoUrl - The URL of the video file to be processed.
     * @param {string} audioUrl - The URL of the audio file to be processed and amplified.
     * @returns {Promise<string>} - A promise that resolves to a Blob URL string of the processed video.
     */
    async processVideo(videoUrl, audioUrl) {
        URL.revokeObjectURL(videoUrl); // Clean up previous blob

        await ffmpeg.load();

        const videoFile = await fetchFile(videoUrl);
        const audioFile = await fetchFile(audioUrl);
        const gainDB = await BeeVideoContainer.calculateAmplificationDB(audioFile);

        await ffmpeg.writeFile('inputVideo.mp4', videoFile);
        await ffmpeg.writeFile('inputAudio.wav', audioFile);

        await ffmpeg.exec([
            "-i", "inputVideo.mp4",
            "-i", "inputAudio.wav",
            "-filter_complex", `[1:a]volume=${gainDB}dB[a]`,
            "-map", "0:v",
            "-map", "[a]",
            "-c:v", "copy",
            "-c:a", "aac",
            "-shortest",
            "output.mp4"
        ]);

        const data = await ffmpeg.readFile('output.mp4');
        await ffmpeg.terminate();

        return URL.createObjectURL(
            new Blob([data.buffer], { type: 'video/mp4' })
        );
    },

    /**
     * Processes the retrieved file path by constructing video and audio URLs,
     * checking if the audio exists, and stitching them together via FFmpeg.
     *
     * @param staticVideoUrl
     * @param staticAudioUrl
     * @param {string} filePath - The file path obtained from the API.
     * @returns {Promise<void>}
     */
    async setVideoUrlAndStitchWithAudio(staticVideoUrl, staticAudioUrl, filePath) {
        const videoPathUrl = staticVideoUrl + BeeVideoContainer.createSpecificVideoUrl(filePath);
        const audioPathUrl = staticAudioUrl + BeeVideoContainer.createSpecificAudioUrl(filePath);

        const audioExists = await BeeVideoContainer.checkDataExists(audioPathUrl);

        if (audioExists) {
            try {
                return await BeeVideoContainer.processVideo(videoPathUrl, audioPathUrl);
            } catch (err) {
                console.error("Error processing via FFmpeg:", err);
                return videoPathUrl; // fallback to unprocessed video
            }
        } else {
            return videoPathUrl; // audio missing, return raw video
        }
    }
};

export default BeeVideoContainer;
